import { Router } from '@angular/router';
import { Injectable, signal, DestroyRef, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import * as fromRoot from '../reducers';
import { constants } from '../constants';

@Injectable()
export class BrowserUtils {
	private readonly destroyRef = inject(DestroyRef);
	private readonly screenWidthSignal = signal(this.getInnerWidth());
	private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
	private resizeHandler: (() => void) | null = null;

	constructor(private readonly store: Store<fromRoot.AppState>, private readonly router: Router) {
		this.initializeResizeListener();
	}

	private getInnerWidth(): number {
		return typeof window !== 'undefined' ? window.innerWidth : 0;
	}

	private initializeResizeListener(): void {
		if (typeof window !== 'undefined') {
			// Store handler as class field so it can be removed later
			this.resizeHandler = () => {
				if (this.resizeTimeout !== null) {
					clearTimeout(this.resizeTimeout);
				}
				this.resizeTimeout = setTimeout(() => {
					this.screenWidthSignal.set(this.getInnerWidth());
					this.resizeTimeout = null;
				}, 250); // Debounce resize events by 250ms
			};

			window.addEventListener('resize', this.resizeHandler);

			// Clean up on destroy
			this.destroyRef.onDestroy(() => {
				if (this.resizeTimeout !== null) {
					clearTimeout(this.resizeTimeout);
					this.resizeTimeout = null;
				}
				if (this.resizeHandler !== null && typeof window !== 'undefined') {
					window.removeEventListener('resize', this.resizeHandler);
					this.resizeHandler = null;
				}
			});
		}
	}

	public getDevice() {
		const isAndroid = navigator.userAgent.toLowerCase().match(/android/i),
			isiPhone = navigator.userAgent.toLowerCase().match(/iphone/i),
			isiPad = navigator.userAgent.toLowerCase().match(/ipad/i);

		if (isAndroid) {
			return constants.DEVICE_TYPE.ANDROID;
		}
		if (isiPhone) {
			return constants.DEVICE_TYPE.IPHONE;
		}
		if (isiPad) {
			return constants.DEVICE_TYPE.IPAD;
		} else {
			return constants.DEVICE_TYPE.DESKTOP;
		}
	}

	public openInNewTab(namedRoute: string) {
		const newRelativeUrl = this.router.createUrlTree([namedRoute]);
		const baseUrl = window.location.href.replace(this.router.url, '');

		window.open(baseUrl + newRelativeUrl, '_blank');
	}

	public getOS() {
		const isMac = navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
		return isMac ? constants.OS_TYPE.IOS : constants.OS_TYPE.ANDROID;
	}

	public screenWidth(): number {
		return this.screenWidthSignal();
	}

	public getAdobeMarketingId(): string {
		const amcvCookie = document.cookie
			.split(';')
			.map(c => c.trim())
			.find(c => c.startsWith('AMCV_'));
		
		if (!amcvCookie) return 'no';
		
		const parts = amcvCookie.split('=');
		if (parts.length < 2 || !parts[1]) return 'no';
		
		return decodeURIComponent(parts[1]);
    }
}
